// src/domains/live/services/liveService.js
import { apiClient } from '@/shared/services/api'

const BASE = '/live-streams'

// ─── Lifecycle ─────────────────────────────────────────────────────────────
export const startLive = (dto = {}) => apiClient.post(BASE, dto)
export const goLive = (id) => apiClient.post(`${BASE}/${id}/go-live`)
export const endLive = (id) => apiClient.post(`${BASE}/${id}/end`)
export const publishLive = (id, saveAsPost) => apiClient.post(`${BASE}/${id}/publish`, { saveAsPost })
export const deleteLive = (id) => apiClient.delete(`${BASE}/${id}`)
export const refreshStreamerToken = (id) => apiClient.post(`${BASE}/${id}/refresh-token`)
export const getMyCurrentLive = () => apiClient.get(`${BASE}/my-current`)

// ─── OBS / RTMP Ingress ──────────────────────────────────────────────────────
// createObsLive returns owner-only credentials: { liveId, roomName, rtmpUrl,
// streamKey, inputType, orientation, status }. `inputType` ('RTMP'|'WHIP') tells
// the UI which OBS fields to label (RTMP Server/Key vs WHIP URL/Bearer Token).
// getObsStatus polls { status, isObsConnected, orientation }. regenerateStreamKey
// returns a fresh { rtmpUrl, streamKey, inputType }.
export const createObsLive = (dto = {}) => apiClient.post(`${BASE}/obs/create`, dto)
export const getObsStatus = (id) => apiClient.get(`${BASE}/${id}/status`)
export const regenerateStreamKey = (id) =>
  apiClient.post(`${BASE}/${id}/obs/regenerate-key`)

// ─── Queries ────────────────────────────────────────────────────────────────
export const getLive = (id) => apiClient.get(`${BASE}/${id}`)
export const getActiveFeed = (params = {}) => apiClient.get(`${BASE}/feed`, { params })
export const getFollowingLive = () => apiClient.get(`${BASE}/following-live`)
export const checkUserLive = (userId) => apiClient.get(`${BASE}/check/${userId}`)
export const getUserLiveHistory = (userId, params = {}) =>
  apiClient.get(`${BASE}/user/${userId}`, { params })
export const getAnalytics = (id) => apiClient.get(`${BASE}/${id}/analytics`)

// ─── Settings ───────────────────────────────────────────────────────────────
export const updateSettings = (id, dto) => apiClient.patch(`${BASE}/${id}/settings`, dto)

// ─── Viewers ─────────────────────────────────────────────────────────────────
export const joinLive = (id) => apiClient.post(`${BASE}/${id}/join`)
export const leaveLive = (id) => apiClient.post(`${BASE}/${id}/leave`)
export const getViewers = (id, params = {}) => apiClient.get(`${BASE}/${id}/viewers`, { params })

// ─── Moderation ──────────────────────────────────────────────────────────────
export const kickViewer = (id, userId, reason) =>
  apiClient.post(`${BASE}/${id}/kick`, { userId, reason })
export const banViewer = (id, userId, reason) =>
  apiClient.post(`${BASE}/${id}/ban`, { userId, reason })
export const unbanViewer = (id, userId) => apiClient.post(`${BASE}/${id}/unban`, { userId })
export const muteViewer = (id, userId, durationSeconds) =>
  apiClient.post(`${BASE}/${id}/mute-viewer`, { userId, durationSeconds })
export const unmuteViewer = (id, userId) =>
  apiClient.post(`${BASE}/${id}/unmute-viewer`, { userId })
export const promoteModerator = (id, userId) =>
  apiClient.post(`${BASE}/${id}/promote-moderator`, { userId })
export const demoteModerator = (id, userId) =>
  apiClient.post(`${BASE}/${id}/demote-moderator`, { userId })
// Banned users (they've left the room, so they're absent from /viewers) — backs
// the host "Banned" tab so the existing unban endpoint is reachable for them.
export const getBannedUsers = (id, params = {}) =>
  apiClient.get(`${BASE}/${id}/banned-users`, { params })

// ─── Guests ──────────────────────────────────────────────────────────────────
export const inviteGuest = (id, userId) =>
  apiClient.post(`${BASE}/${id}/invite-guest`, { userId })
export const acceptGuestInvite = (id) =>
  apiClient.post(`${BASE}/${id}/accept-guest`)
export const declineGuestInvite = (id) =>
  apiClient.post(`${BASE}/${id}/decline-guest`)
export const removeGuest = (id, userId) =>
  apiClient.post(`${BASE}/${id}/remove-guest`, { userId })
export const muteGuestAudio = (id, userId) =>
  apiClient.post(`${BASE}/${id}/mute-guest-audio`, { userId })
export const unmuteGuestAudio = (id, userId) =>
  apiClient.post(`${BASE}/${id}/unmute-guest-audio`, { userId })
export const muteGuestVideo = (id, userId) =>
  apiClient.post(`${BASE}/${id}/mute-guest-video`, { userId })
export const unmuteGuestVideo = (id, userId) =>
  apiClient.post(`${BASE}/${id}/unmute-guest-video`, { userId })
export const requestToSpeak = (id) =>
  apiClient.post(`${BASE}/${id}/request-speak`)
export const acceptSpeakRequest = (id, userId) =>
  apiClient.post(`${BASE}/${id}/accept-speak`, { userId })
export const getSpeakRequests = (id) => apiClient.get(`${BASE}/${id}/speak-requests`)
export const rejectSpeakRequest = (id, userId) =>
  apiClient.post(`${BASE}/${id}/reject-speak`, { userId })

// ─── Comments ────────────────────────────────────────────────────────────────
export const addComment = (id, content, parentId) =>
  apiClient.post(`${BASE}/${id}/comments`, { content, parentId })
export const getComments = (id, params = {}) =>
  apiClient.get(`${BASE}/${id}/comments`, { params })
export const getCommentsReplay = (id, fromSec, toSec) =>
  apiClient.get(`${BASE}/${id}/comments/replay`, { params: { from: fromSec, to: toSec } })
export const pinComment = (id, commentId) =>
  apiClient.patch(`${BASE}/${id}/comments/${commentId}/pin`)
export const unpinComment = (id, commentId) =>
  apiClient.patch(`${BASE}/${id}/comments/${commentId}/unpin`)
export const deleteComment = (id, commentId) =>
  apiClient.delete(`${BASE}/${id}/comments/${commentId}`)
export const reportComment = (id, commentId, reason) =>
  apiClient.post(`${BASE}/${id}/comments/${commentId}/report`, { reason })

// ─── Share / Report ──────────────────────────────────────────────────────────
export const shareLive = (id, conversationIds) =>
  apiClient.post(`${BASE}/${id}/share`, { conversationIds })
export const getShareLink = (id) => apiClient.get(`${BASE}/${id}/share-link`)
export const reportLive = (id, reason) => apiClient.post(`${BASE}/${id}/report`, { reason })

// ─── Media ───────────────────────────────────────────────────────────────────
export const getRecordingDownloadUrl = (id) => apiClient.get(`${BASE}/${id}/recording-url`)
export const getThumbnailUploadUrl = (id, body = {}) =>
  apiClient.post(`${BASE}/${id}/thumbnail-upload-url`, body)

// ─── Reminders ───────────────────────────────────────────────────────────────
export const addReminder = (id) => apiClient.post(`${BASE}/${id}/remind-me`)
export const removeReminder = (id) => apiClient.delete(`${BASE}/${id}/remind-me`)
export const getReminderCount = (id) => apiClient.get(`${BASE}/${id}/reminder-count`)
